import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from 'src/utils/axios';
import { getUser } from 'src/utils/Common';
import { postEventStream, isAbortError } from 'src/utils/sseClient';
import { dosyaTuruBul, base64Bytes, dosyaIndir } from 'src/utils/dokumanTuru';
import {
    Alert,
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { keyframes } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';

/**
 * Teknisyen Koçu sohbet penceresi — Artemis'teki TeknisyenKocuPopUp'ın Apollo karşılığı.
 *
 * Sohbet iki şekilde açılır:
 *   - Ürünle   (userModelCode): dokümanları backend ürün koduna göre bulur, hızlı sorular gösterilir.
 *   - Dokümanla (bankaNo):      yalnızca verilen doküman(lar) kullanılır (Döküman Yönetimi test sohbeti).
 * Artemis'ten farklı olarak geri bildirim (beğendim/beğenmedim) ve montaj adımları yok.
 */

/**
 * true  -> /ChatWithProductStream (SSE, cevap parça parça yazılır)
 * false -> /ChatWithProduct       (tek seferlik klasik istek)
 * Stream tarafında sorun yaşanırsa bu bayrağı false'a çekmek eski davranışa dönmek için yeterli.
 */
const USE_CHAT_STREAM = true;

/**
 * Cevap metninin ekrana yazılma temposu. Upstream bugün cevabın tamamını hazırlayıp tüm
 * parçaları ~250 ms içinde topluca gönderiyor; bu tampon metni okunabilir bir tempoda yayar.
 * Hız, bekleyen metin miktarından hesaplandığı için gerçek akışta gecikme sıfıra yakın kalır.
 */
const TYPE_DRAIN_SECONDS = 3.5;
const TYPE_MIN_CPS = 220;
const TYPE_MAX_CPS = 1400;

const GENERIC_ERROR_TEXT = 'Şu anda isteğinizi işlerken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.';
const NO_ANSWER_TEXT = 'Üzgünüm, bu konuda yardımcı olabileceğim bir bilgiye ulaşamadım.';
const WELCOME_MSG_ID = 'welcome_agent_msg';

// Konuşmalar tarayıcıda saklanır. Eski sürümün 'conversations' kaydı farklı biçimde olduğu için
// yeni anahtar kullanılıyor.
const CONVERSATIONS_KEY = 'tkSohbetGecmisi';
const QUICK_QUESTIONS_KEY = 'tkHizliSorular';

// Backend'in "event: status" ile bildirdiği bekleme aşamaları.
const WAITING_STAGE_TEXTS = {
    receipt: 'Bilgiler kontrol ediliyor',
    documents: 'İlgili dokümanlar taranıyor',
    thinking: 'Düşünüyor'
};

const RENK = {
    turuncu: '#FF9800',
    turuncuKoyu: '#F57C00',
    teal: '#009688',
    tealKoyu: '#00695C',
    tealZemin: '#E0F2F1',
    tealZeminHover: '#B2DFDB',
    zemin: '#f4f6f8'
};

const SAAT_FORMATI = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
const nowHHmm = () => new Date().toLocaleTimeString('tr-TR', SAAT_FORMATI);
const formatTime = (isoString) => new Date(isoString).toLocaleTimeString('tr-TR', SAAT_FORMATI);

// Baloncuk hiçbir koşulda boş kalmamalı: adaylardan ilk dolu olanı seçer.
const firstNonBlank = (...values) =>
    values.find(value => typeof value === 'string' && value.trim().length > 0) || NO_ANSWER_TEXT;

// Yanıt alan adları PascalCase ya da camelCase gelebiliyor; ilk dolu olanı döner.
const pickField = (source, ...names) => {
    if (!source) return undefined;
    for (const name of names) {
        const value = source[name];
        if (value !== undefined && value !== null) return value;
    }
    return undefined;
};

const normalizeDocuments = (documents) => {
    if (!Array.isArray(documents)) return [];
    return documents.map(doc => ({
        DocumentName: pickField(doc, 'DocumentName', 'documentName'),
        PageNumber: pickField(doc, 'PageNumber', 'pageNumber'),
        BankNo: pickField(doc, 'BankNo', 'bankNo'),
        DocumentDescription: pickField(doc, 'DocumentDescription', 'documentDescription')
    }));
};

const normalizeChatResponse = (source) => ({
    reply: pickField(source, 'Response', 'response'),
    convId: pickField(source, 'ConvId', 'convId') ?? null,
    documents: normalizeDocuments(pickField(source, 'Documents', 'documents'))
});

const storageOku = (storage, key, varsayilan) => {
    try {
        const raw = storage.getItem(key);
        return raw ? JSON.parse(raw) : varsayilan;
    } catch (e) {
        return varsayilan;
    }
};

const storageYaz = (storage, key, value) => {
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch (e) {
        storage.removeItem(key);
    }
};

const escapeHtml = (text) => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Cevaptaki sade markdown'ı (başlık, kalın, madde) HTML'e çevirir. Metin önce kaçışlanır;
// dangerouslySetInnerHTML ile basıldığı için model ya da kullanıcı metnindeki HTML çalıştırılmaz.
function convertMarkdown(md) {
    if (!md) return "";
    md = escapeHtml(md.replace(/\\n/g, "\n").replace(/^\s+|\s+$/g, ""));

    const applyInline = t => t.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    let html = "";
    let listOpen = false;
    let subListOpen = false;

    const closeSubList = () => {
        if (subListOpen) { html += "</ul>"; subListOpen = false; }
    };
    const closeList = () => {
        closeSubList();
        if (listOpen) { html += "</ul>"; listOpen = false; }
    };

    for (const line of md.split("\n")) {
        if (/^\s*-{3,}\s*$/.test(line)) continue;

        if (!line.trim()) {
            closeList();
            html += '<div style="height:4px"></div>';
            continue;
        }

        const hMatch = line.match(/^\s*(#{1,4})\s*(.+)$/);
        if (hMatch) {
            closeList();
            const sizes = { 1: '1.45rem', 2: '1.35rem', 3: '1.25rem', 4: '1.15rem' };
            html += `<p style="margin:10px 0 3px;font-size:${sizes[hMatch[1].length]};font-weight:700;color:#111">${applyInline(hMatch[2])}</p>`;
            continue;
        }

        const numMatch = line.match(/^(\d+)\.\s+(.+)$/);
        if (numMatch) {
            closeList();
            html += `<p style="margin:8px 0 2px;font-size:1.05rem;font-weight:700;color:#111">${numMatch[1]}. ${applyInline(numMatch[2])}</p>`;
            continue;
        }

        const subMatch = line.match(/^ +- (.+)$/);
        if (subMatch) {
            if (!listOpen) { html += '<ul style="margin:2px 0;padding-left:8px;list-style:none">'; listOpen = true; }
            if (!subListOpen) { html += '<ul style="margin:0;padding-left:16px;list-style:none">'; subListOpen = true; }
            html += `<li style="margin:1px 0;color:#555;font-size:0.87rem">– ${applyInline(subMatch[1])}</li>`;
            continue;
        }

        const bulletMatch = line.match(/^- (.+)$/);
        if (bulletMatch) {
            closeSubList();
            if (!listOpen) { html += '<ul style="margin:2px 0;padding-left:8px;list-style:none">'; listOpen = true; }
            html += `<li style="margin:3px 0">– ${applyInline(bulletMatch[1])}</li>`;
            continue;
        }

        closeList();
        html += `<p style="margin:2px 0">${applyInline(line)}</p>`;
    }

    closeList();
    return html;
}

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`;

function TypingIndicator() {
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", py: 0.5 }}>
            {[0, 0.2, 0.4].map((delay, i) => (
                <Box
                    key={i}
                    component="span"
                    sx={{
                        width: 6,
                        height: 6,
                        mx: '2px',
                        borderRadius: "100%",
                        bgcolor: "#888383de",
                        animation: `${bounce} 1.4s infinite ease-in-out`,
                        animationDelay: `${delay}s`
                    }}
                />
            ))}
        </Box>
    );
}

// Baloncuk köşesindeki küçük üçgen.
const balonKuyrugu = (taraf) => ({
    content: '""',
    position: 'absolute',
    top: 0,
    [taraf]: '-9px',
    width: 0,
    height: 0,
    borderTop: '10px solid #ffffff',
    [taraf === 'left' ? 'borderLeft' : 'borderRight']: '10px solid transparent'
});

const balonStili = (isAgent) => ({
    position: 'relative',
    p: 1.5,
    overflow: 'visible',
    bgcolor: '#ffffff',
    color: 'text.primary',
    filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.1))',
    borderRadius: isAgent ? '0px 12px 12px 12px' : '12px 0px 12px 12px',
    ml: isAgent ? 1 : 0,
    mr: isAgent ? 0 : 1,
    '&::before': balonKuyrugu(isAgent ? 'left' : 'right')
});

function TeknisyenGPTDialog({ open, onClose, bankaNo, userModelCode, urunAciklama }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const user = getUser();

    const urunModu = !!userModelCode;
    // Konuşma geçmişi sohbetin açıldığı ürün ya da doküman bazında tutulur.
    const sohbetAnahtari = urunModu ? `urun:${userModelCode}` : `dokuman:${bankaNo || ''}`;

    const endRef = useRef(null);
    const contentRef = useRef(null);
    const isNearBottomRef = useRef(true);
    const programmaticScrollRef = useRef(false);
    const streamAbortRef = useRef(null);
    const isSendingRef = useRef(false);
    const isMountedRef = useRef(true);

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [waitingStage, setWaitingStage] = useState(null);
    const [convId, setConvId] = useState(null);
    const [documentLoading, setDocumentLoading] = useState(null);
    const [isTokenLoading, setIsTokenLoading] = useState(false);
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const welcomeText = `${user?.FirstName || 'Merhaba'}${user?.FirstName ? ' merhaba' : ''}, ben senin yapay zeka asistanınım. ` +
        (urunModu
            ? 'Bu ürünle ilgili dersime çalıştım. Karşılaştığın sorun ile ilgili bana her tür soruyu sorabilirsin.'
            : 'Bu dokümana çalıştım. Dokümanla ilgili bana her tür soruyu sorabilirsin.');

    // ---- Konuşma geçmişi ----------------------------------------------------------

    const getConversation = useCallback(() => {
        return storageOku(localStorage, CONVERSATIONS_KEY, []).find(c => c.Anahtar === sohbetAnahtari) || null;
    }, [sohbetAnahtari]);

    const saveConversation = useCallback((conv_id, soru, cevap, documents = []) => {
        const tumu = storageOku(localStorage, CONVERSATIONS_KEY, []);
        let konusma = tumu.find(c => c.Anahtar === sohbetAnahtari);

        if (!konusma) {
            konusma = { Anahtar: sohbetAnahtari, conv_id: conv_id || null, KonusmaGecmisi: [] };
            tumu.push(konusma);
        }
        if (conv_id && !konusma.conv_id) {
            konusma.conv_id = conv_id;
        }
        if (soru || cevap) {
            konusma.KonusmaGecmisi.push({ tarih: new Date().toISOString(), soru, cevap, documents });
        }
        storageYaz(localStorage, CONVERSATIONS_KEY, tumu);
    }, [sohbetAnahtari]);

    // ---- Token --------------------------------------------------------------------

    const isTokenAboutToExpire = () => {
        const tokenInfo = storageOku(localStorage, 'tk_token_info', null);
        const exp = Number(tokenInfo?.expiresAt);
        if (!tokenInfo?.accessToken || Number.isNaN(exp)) return true;
        return Date.now() > exp - 60000;
    };

    const getNewToken = () => {
        setIsTokenLoading(true);
        return axios.get('/TeknisyenKocuLogin')
            .then((response) => {
                const data = response.data;
                const tokenInfo = {
                    accessToken: data.access_token,
                    tokenType: data.token_type,
                    expiresAt: Date.now() + data.expires_in * 1000
                };
                storageYaz(localStorage, 'tk_token_info', tokenInfo);
                return tokenInfo;
            })
            .catch((err) => {
                console.error('Teknisyen Koçu token alınamadı:', err);
                return null;
            })
            .finally(() => setIsTokenLoading(false));
    };

    // Token yenilenirken localStorage'daki değer hâlâ eski olabileceği için istekten önce beklenir.
    const ensureAccessToken = async () => {
        if (isTokenAboutToExpire()) {
            const tokenInfo = await getNewToken();
            if (tokenInfo?.accessToken) return tokenInfo.accessToken;
        }
        return storageOku(localStorage, 'tk_token_info', null)?.accessToken || null;
    };

    // ---- Hızlı sorular (yalnızca ürünle açılan sohbette) --------------------------

    const hizliSorulariUygula = (sorular) => {
        setMessages(prev => prev.map(m => (m.id === WELCOME_MSG_ID ? { ...m, quickQuestions: sorular } : m)));
    };

    const handleGetQuickQuestions = () => {
        const kayitli = storageOku(sessionStorage, QUICK_QUESTIONS_KEY, null);
        if (Array.isArray(kayitli) && kayitli.length > 0) {
            hizliSorulariUygula(kayitli);
            return;
        }
        axios.get('/GetTeknisyenKocuQuickQuestions')
            .then((response) => {
                const sorular = (Array.isArray(response.data) ? response.data : [])
                    .map(x => x.USER_QUESTION)
                    .filter(Boolean);
                storageYaz(sessionStorage, QUICK_QUESTIONS_KEY, sorular);
                if (isMountedRef.current) hizliSorulariUygula(sorular);
            })
            .catch((ex) => console.error('Hızlı sorular alınamadı:', ex));
    };

    const karsilamaMesajiniGoster = () => {
        setMessages([{
            id: WELCOME_MSG_ID,
            sender: 'agent',
            text: welcomeText,
            time: nowHHmm(),
            documents: [],
            quickQuestions: []
        }]);
        setConvId(null);
        if (urunModu) handleGetQuickQuestions();
    };

    // ---- Açılış --------------------------------------------------------------------

    useEffect(() => {
        if (!open) return;
        if (isTokenAboutToExpire()) getNewToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open || (!userModelCode && !bankaNo)) return;

        const konusma = getConversation();
        if (!konusma || konusma.KonusmaGecmisi.length === 0) {
            karsilamaMesajiniGoster();
            return;
        }

        setConvId(konusma.conv_id);
        setMessages([
            // Karşılama mesajı geçmişe yazılmaz, her açılışta yeniden eklenir.
            { id: WELCOME_MSG_ID, sender: 'agent', text: welcomeText, time: formatTime(konusma.KonusmaGecmisi[0].tarih), documents: [], quickQuestions: [] },
            ...konusma.KonusmaGecmisi.flatMap((msg, i) => ([
                { id: i + '_c', sender: 'customer', text: msg.soru, time: formatTime(msg.tarih) },
                { id: i + '_a', sender: 'agent', text: msg.cevap, time: formatTime(msg.tarih), documents: normalizeDocuments(msg.documents) }
            ]))
        ]);
        if (urunModu) handleGetQuickQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sohbetAnahtari]);

    // Popup kapanınca ya da bileşen kaldırılınca açık kalan stream bağlantısı kesilir.
    useEffect(() => {
        if (!open && streamAbortRef.current) {
            streamAbortRef.current.abort();
            streamAbortRef.current = null;
        }
    }, [open]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (streamAbortRef.current) {
                streamAbortRef.current.abort();
                streamAbortRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!isTyping) setWaitingStage(null);
    }, [isTyping]);

    // ---- Kaydırma ------------------------------------------------------------------

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        if (messages.some(m => m.isStreaming)) {
            // Metin sürekli büyüdüğü için smooth scroll titretir; kullanıcı en alttaysa anlık
            // takip edilir, yukarı çıkmışsa konumu bozulmadan "aşağı in" butonu gösterilir.
            const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
            if (isNearBottomRef.current) {
                programmaticScrollRef.current = true;
                el.scrollTop = el.scrollHeight;
                setShowScrollBtn(false);
            } else {
                isNearBottomRef.current = atBottom;
                setShowScrollBtn(!atBottom);
            }
            return;
        }

        if (isNearBottomRef.current) {
            programmaticScrollRef.current = true;
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            setShowScrollBtn(false);
        }
    }, [messages, isTyping, open]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return undefined;
        const handleScroll = () => {
            const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
            if (programmaticScrollRef.current) {
                if (atBottom) programmaticScrollRef.current = false;
                return;
            }
            isNearBottomRef.current = atBottom;
            setShowScrollBtn(!atBottom);
        };
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [open]);

    // ---- Gönderim ------------------------------------------------------------------

    const musteriMetni = (text) => {
        if (!urunModu || !urunAciklama) return text;
        const model = urunAciklama.length > 25 ? urunAciklama.slice(0, 25) + '…' : urunAciklama;
        return `${text}\n(Ürün: ${model})`;
    };

    const istekGovdesi = (text, accessToken) => ({
        input: text,
        conv_id: convId,
        bankNo: urunModu ? null : bankaNo,
        UserModelCode: urunModu ? userModelCode : null,
        accessToken
    });

    const handleSend = async (text) => {
        const gorunenMetin = musteriMetni(text);
        setMessages(prev => [...prev, { id: Date.now() + '_c', sender: 'customer', text: gorunenMetin, time: nowHHmm() }]);
        setInput('');
        setIsTyping(true);
        isSendingRef.current = true;
        setIsStreaming(true);

        try {
            const accessToken = await ensureAccessToken();
            const res = await axios.post('/ChatWithProduct', istekGovdesi(text, accessToken));
            const { reply, convId: yeniConvId, documents } = normalizeChatResponse(res?.data);
            const cevap = firstNonBlank(reply);

            if (yeniConvId && !convId) setConvId(yeniConvId);
            setMessages(prev => [...prev, { id: Date.now() + '_a', sender: 'agent', text: cevap, time: nowHHmm(), documents }]);
            saveConversation(yeniConvId, gorunenMetin, cevap, documents);
        } catch (ex) {
            console.error('Sohbet hatası:', ex);
            setMessages(prev => [...prev, { id: Date.now() + '_err', sender: 'agent', text: GENERIC_ERROR_TEXT, time: nowHHmm() }]);
        } finally {
            isSendingRef.current = false;
            if (isMountedRef.current) {
                setIsTyping(false);
                setIsStreaming(false);
            }
        }
    };

    /**
     * Stream (SSE) gönderim. Backend event'leri: status, chunk (düz metin), final (ChatResponse),
     * error ({message}), done. Hızlı soru gibi durumlarda chunk hiç gelmeden final düşer; bu yüzden
     * balon ilk içerik geldiğinde oluşturulur.
     */
    const handleSendStream = async (text) => {
        const gorunenMetin = musteriMetni(text);
        setMessages(prev => [...prev, { id: Date.now() + '_c', sender: 'customer', text: gorunenMetin, time: nowHHmm() }]);
        setInput('');

        isSendingRef.current = true;
        setIsStreaming(true);
        setIsTyping(true);

        const streamMsgId = Date.now() + '_a';
        let bubbleAdded = false;
        let streamedText = '';
        let finalHandled = false;

        // Daktilo tamponu: targetText gelen metni, shownLen ekranda görüneni tutar.
        let targetText = '';
        let shownLen = 0;
        let typeFrame = null;
        let lastTick = 0;
        let completion = null;
        let completionTimer = null;

        const stopTyping = () => {
            if (typeFrame !== null) {
                cancelAnimationFrame(typeFrame);
                typeFrame = null;
            }
            if (completionTimer !== null) {
                clearTimeout(completionTimer);
                completionTimer = null;
            }
        };

        const releaseUi = () => {
            isSendingRef.current = false;
            if (isMountedRef.current) {
                setIsStreaming(false);
                setIsTyping(false);
            }
        };

        const writeToBubble = (bubbleText, extraProps) => {
            setMessages(prev => prev.map(m => (m.id === streamMsgId ? { ...m, text: bubbleText, ...extraProps } : m)));
        };

        const applyCompletion = () => {
            const props = completion || {};
            completion = null;
            stopTyping();
            if (!isMountedRef.current) {
                isSendingRef.current = false;
                return;
            }
            writeToBubble(targetText, { isStreaming: false, ...props });
            releaseUi();
        };

        const tick = (ts) => {
            typeFrame = null;
            if (!isMountedRef.current) return;

            // Sekme arka plandan dönünce oluşan dev aralık sıçramaya yol açmasın.
            const dt = lastTick ? Math.min(ts - lastTick, 250) : 16;
            lastTick = ts;

            const backlog = targetText.length - shownLen;
            if (backlog > 0) {
                const cps = Math.min(TYPE_MAX_CPS, Math.max(TYPE_MIN_CPS, backlog / TYPE_DRAIN_SECONDS));
                shownLen = Math.min(targetText.length, shownLen + Math.max(1, Math.round((cps * dt) / 1000)));
                writeToBubble(targetText.slice(0, shownLen));
            }

            if (shownLen >= targetText.length) {
                if (completion) applyCompletion();
                return;
            }
            typeFrame = requestAnimationFrame(tick);
        };

        const ensureTyping = () => {
            if (typeFrame !== null) return;
            lastTick = 0;
            typeFrame = requestAnimationFrame(tick);
        };

        const addBubble = (initialText, extraProps = {}) => {
            bubbleAdded = true;
            setIsTyping(false);
            setMessages(prev => [...prev, {
                id: streamMsgId,
                sender: 'agent',
                text: initialText,
                time: nowHHmm(),
                documents: [],
                isStreaming: true,
                ...extraProps
            }]);
        };

        // Balonun nihai metnini belirler. immediate=true ise (hata mesajı) daktilo beklenmez.
        const showAgentText = (finalText, extraProps = {}, immediate = false) => {
            const metin = finalText || '';

            if (!bubbleAdded) {
                stopTyping();
                targetText = metin;
                shownLen = metin.length;
                addBubble(metin, { isStreaming: false, ...extraProps });
                return;
            }

            // Final metin akan metinden farklı olabilir (kapanış notu temizleniyor). Ekrandaki
            // kısım hâlâ önek ise yazma kaldığı yerden sürer; değilse metnin tamamı gösterilir.
            const shownSoFar = targetText.slice(0, shownLen);
            targetText = metin;
            if (!metin.startsWith(shownSoFar)) {
                shownLen = metin.length;
            }

            completion = { ...extraProps };
            const remaining = targetText.length - shownLen;

            if (immediate || remaining <= 0) {
                shownLen = targetText.length;
                applyCompletion();
                return;
            }

            ensureTyping();

            // rAF sekme arka plandayken çalışmaz; balon yarım kalmasın diye tamamlanma
            // setTimeout'a da bağlanır.
            const expectedMs = Math.min(TYPE_DRAIN_SECONDS, remaining / TYPE_MIN_CPS) * 1000;
            completionTimer = setTimeout(() => {
                completionTimer = null;
                if (completion) {
                    shownLen = targetText.length;
                    applyCompletion();
                }
            }, expectedMs + 500);
        };

        const controller = new AbortController();
        streamAbortRef.current = controller;

        try {
            const accessToken = await ensureAccessToken();

            await postEventStream('/ChatWithProductStream', istekGovdesi(text, accessToken), {
                signal: controller.signal,
                onEvent: ({ event, data }) => {
                    if (event === 'status') {
                        try {
                            const statusData = JSON.parse(data);
                            if (statusData?.stage) setWaitingStage(statusData.stage);
                        } catch (e) {
                            // okunamazsa bekleme metni olduğu gibi kalır
                        }
                        return;
                    }

                    if (event === 'chunk') {
                        if (finalHandled) return;
                        streamedText += data;
                        targetText = streamedText;
                        // İlk parça yalnızca boşluk olabiliyor; görünür içerik gelene kadar balon açılmaz.
                        if (!bubbleAdded && streamedText.trim()) {
                            shownLen = 1;
                            addBubble(targetText.slice(0, shownLen));
                        }
                        if (bubbleAdded) ensureTyping();
                        return;
                    }

                    if (event === 'final') {
                        let finalData;
                        try {
                            finalData = JSON.parse(data);
                        } catch (e) {
                            console.error('Stream final verisi çözümlenemedi:', e);
                            return;
                        }

                        finalHandled = true;
                        const { reply, convId: yeniConvId, documents } = normalizeChatResponse(finalData);
                        const cevap = firstNonBlank(reply, streamedText);

                        if (yeniConvId && !convId) setConvId(yeniConvId);

                        showAgentText(cevap, { documents, time: nowHHmm() });
                        saveConversation(yeniConvId, gorunenMetin, cevap, documents);
                        return;
                    }

                    if (event === 'error') {
                        finalHandled = true;
                        let errText = GENERIC_ERROR_TEXT;
                        try {
                            errText = JSON.parse(data).message || errText;
                        } catch (e) {
                            // gövde JSON değilse varsayılan mesaj
                        }
                        showAgentText(errText, {}, true);
                    }
                }
            });

            // Akış final/error görmeden kapandıysa: elde metin varsa korunur.
            if (!finalHandled) {
                if (streamedText.trim()) {
                    showAgentText(streamedText);
                    saveConversation(convId, gorunenMetin, streamedText, []);
                } else {
                    showAgentText(GENERIC_ERROR_TEXT, {}, true);
                }
            }
        } catch (ex) {
            stopTyping();
            completion = null;

            if (isAbortError(ex)) {
                // Popup kapatıldı / sohbet sıfırlandı: gelen metin yarım daktilo bırakılmadan gösterilir.
                if (isMountedRef.current && bubbleAdded) {
                    writeToBubble(targetText, { isStreaming: false });
                }
                return;
            }

            console.error('Stream sohbet hatası:', ex);
            if (isMountedRef.current && !ex?.handled) {
                showAgentText(firstNonBlank(streamedText, GENERIC_ERROR_TEXT), {}, true);
            }
        } finally {
            if (streamAbortRef.current === controller) {
                streamAbortRef.current = null;
            }
            // Daktilo hâlâ yazıyorsa kilidi o açar; yoksa burada açılır.
            if (typeFrame === null && completion === null) {
                releaseUi();
            }
        }
    };

    const sendMessage = (hazirMetin) => {
        const text = (typeof hazirMetin === 'string' ? hazirMetin : input).trim();
        if (!text || isSendingRef.current) return;
        isNearBottomRef.current = true;
        (USE_CHAT_STREAM ? handleSendStream : handleSend)(text);
    };

    const clearConversation = () => {
        // Devam eden stream kesilir; aksi hâlde gelen parçalar yeni listeye yazılmaya çalışır.
        if (streamAbortRef.current) {
            streamAbortRef.current.abort();
            streamAbortRef.current = null;
        }
        storageYaz(localStorage, CONVERSATIONS_KEY,
            storageOku(localStorage, CONVERSATIONS_KEY, []).filter(c => c.Anahtar !== sohbetAnahtari));
        karsilamaMesajiniGoster();
    };

    // ---- Kaynak doküman --------------------------------------------------------------

    const handleDownloadDocument = (bankNo, pageNo) => {
        setDocumentLoading(`${bankNo}-${pageNo}`);
        axios.get(`/GetTeknisyenKocuRelatedDocument/${bankNo}`)
            .then((response) => {
                // Apollo bu uçta [{ DocumentData }] listesi dönüyor (JSON string olarak da gelebilir).
                let veri = response.data;
                if (typeof veri === 'string') {
                    try { veri = JSON.parse(veri); } catch (e) { /* düz base64 */ }
                }
                const base64Data = Array.isArray(veri) ? veri[0]?.DocumentData : veri;
                if (!base64Data) throw new Error('Doküman içeriği boş.');

                // Doküman PDF olmayabilir; uzantı içerikten bulunur.
                const byteArray = base64Bytes(base64Data);
                const tur = dosyaTuruBul(byteArray);
                if (!tur.destekleniyor) throw new Error(`Desteklenmeyen dosya türü: ${tur.etiket}`);
                const url = URL.createObjectURL(new Blob([byteArray], { type: tur.mime }));
                dosyaIndir(url, `${bankNo}${tur.uzanti}`);
                URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error('Döküman açılırken hata oluştu:', error);
                setSnackbar({ open: true, message: 'Döküman açılırken hata oluştu.', severity: 'error' });
            })
            .finally(() => setDocumentLoading(null));
    };

    // ---- Görünüm -------------------------------------------------------------------

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : '16px',
                    height: fullScreen ? '100%' : '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: '#fff'
                }
            }}
        >
            <DialogTitle sx={{ p: 2, bgcolor: '#ffffff' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                        <Avatar sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', color: RENK.turuncu, width: 40, height: 40 }}>
                            <ContactSupportIcon />
                        </Avatar>
                        <Box minWidth={0}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                                Teknisyen Koçu
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50', flexShrink: 0 }} />
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Çevrimiçi{urunModu ? (urunAciklama ? ` · ${urunAciklama}` : '') : (bankaNo ? ` · Doküman ${bankaNo}` : '')}
                                </Box>
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={(e) => { e.stopPropagation(); onClose(); }} size="small" sx={{ color: '#757575' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />

            <DialogContent
                ref={contentRef}
                sx={{
                    flex: 1,
                    bgcolor: RENK.zemin,
                    p: 2,
                    pt: 4,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Box sx={{ width: '100%', mt: 1 }} />
                {messages.map((m) => {
                    const isAgent = m.sender === 'agent';
                    return (
                        <Box
                            key={m.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: isAgent ? 'flex-start' : 'flex-end',
                                mb: 2,
                                width: '100%'
                            }}
                        >
                            {isAgent && (
                                <Avatar sx={{ width: 32, height: 32, bgcolor: RENK.teal }}>
                                    <ContactSupportIcon sx={{ fontSize: '1rem' }} />
                                </Avatar>
                            )}
                            <Box sx={{ maxWidth: { xs: '85%', sm: '75%' }, display: 'flex', flexDirection: 'column' }}>
                                <Paper elevation={0} sx={balonStili(isAgent)}>
                                    <Typography
                                        variant="body1"
                                        component="div"
                                        sx={{ wordBreak: 'break-word', fontSize: '0.90rem', lineHeight: 1.5, textAlign: 'justify' }}
                                    >
                                        <span dangerouslySetInnerHTML={{ __html: convertMarkdown(m.text) }} />
                                    </Typography>

                                    {isAgent && m.quickQuestions?.length > 0 && (
                                        <>
                                            <Box sx={{ mt: '14px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box component="span" sx={{ flexShrink: 0, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: RENK.tealKoyu }}>
                                                    Bunları da sorabilirsin
                                                </Box>
                                                <Box component="span" sx={{ flex: 1, height: '1px', bgcolor: RENK.tealZemin }} />
                                            </Box>
                                            <Box sx={{ mt: '10px', display: 'flex', flexWrap: 'wrap' }}>
                                                {m.quickQuestions.map((q, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={q}
                                                        clickable
                                                        disabled={isStreaming}
                                                        onClick={() => sendMessage(q)}
                                                        sx={{
                                                            bgcolor: RENK.tealZemin,
                                                            color: RENK.tealKoyu,
                                                            fontSize: '0.80rem',
                                                            fontWeight: 500,
                                                            borderRadius: '16px',
                                                            mr: '10px',
                                                            mb: '10px',
                                                            height: 'auto',
                                                            '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                                                            '&:hover': { bgcolor: RENK.tealZeminHover }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </>
                                    )}

                                    {isAgent && m.documents?.length > 0 && (
                                        <Box mt={1.5} display="flex" flexDirection="column">
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#757575', mb: 0.5 }}>
                                                Bu bilgi için aşağıdaki dökümanlardan yararlanılmıştır:
                                            </Typography>
                                            {m.documents.map((doc, idx) => {
                                                const isLoading = documentLoading === `${doc.BankNo}-${doc.PageNumber}`;
                                                return (
                                                    <Paper
                                                        key={idx}
                                                        elevation={0}
                                                        onClick={() => !isLoading && handleDownloadDocument(doc.BankNo, doc.PageNumber)}
                                                        sx={{
                                                            p: 0.8,
                                                            mt: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            cursor: isLoading ? 'wait' : 'pointer',
                                                            bgcolor: '#f9fafb',
                                                            border: '1px solid #eee',
                                                            '&:hover': { bgcolor: '#f0f0f0' }
                                                        }}
                                                    >
                                                        {isLoading
                                                            ? <CircularProgress size={14} sx={{ mr: 1, color: '#f44336' }} />
                                                            : <DescriptionIcon sx={{ fontSize: 18, color: '#f44336', mr: 1 }} />}
                                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                            {doc.DocumentDescription || doc.DocumentName}
                                                        </Typography>
                                                    </Paper>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', mt: 1, textAlign: 'right', fontSize: '0.7rem', opacity: 0.8, lineHeight: 1, color: 'rgba(18, 17, 17, 0.9)' }}
                                    >
                                        {m.time}
                                    </Typography>
                                </Paper>
                            </Box>
                            {!isAgent && (
                                <Avatar sx={{ width: 32, height: 32, bgcolor: RENK.turuncu }}>
                                    <PersonIcon sx={{ fontSize: '1rem' }} />
                                </Avatar>
                            )}
                        </Box>
                    );
                })}

                {isTyping && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, width: '100%' }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: RENK.teal }}>
                            <ContactSupportIcon sx={{ fontSize: '1rem' }} />
                        </Avatar>
                        <Paper elevation={0} sx={balonStili(true)}>
                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                {/* İlk kelime 25-30 sn sonra gelebildiği için bekleme aşaması gösterilir. */}
                                {waitingStage && (
                                    <Typography variant="caption" sx={{ color: '#78909c', fontSize: '0.75rem', lineHeight: 1.2, mb: '2px' }}>
                                        {WAITING_STAGE_TEXTS[waitingStage] || 'Düşünüyor'}
                                    </Typography>
                                )}
                                <TypingIndicator />
                            </Box>
                        </Paper>
                    </Box>
                )}

                {showScrollBtn && (
                    <IconButton
                        size="small"
                        onClick={() => {
                            isNearBottomRef.current = true;
                            setShowScrollBtn(false);
                            endRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        sx={{
                            position: 'sticky',
                            bottom: 16,
                            alignSelf: 'flex-end',
                            zIndex: 10,
                            bgcolor: RENK.turuncu,
                            color: '#fff',
                            width: 36,
                            height: 36,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            '&:hover': { bgcolor: RENK.turuncuKoyu }
                        }}
                    >
                        <KeyboardArrowDownIcon />
                    </IconButton>
                )}
                <div ref={endRef} />
            </DialogContent>
            <Divider />

            <DialogActions sx={{ p: '10px 12px', bgcolor: '#fff' }}>
                <Box display="flex" alignItems="center" width="100%" gap={1}>
                    <Tooltip title="Yeni Sohbet Başlat">
                        <IconButton
                            onClick={clearConversation}
                            sx={{
                                width: 40,
                                height: 40,
                                p: 0,
                                flexShrink: 0,
                                bgcolor: '#fff0f0',
                                border: '1.5px solid #ffcccc',
                                borderRadius: '12px',
                                '&:hover': { bgcolor: '#ffe3e3' }
                            }}
                        >
                            <DeleteSweepIcon sx={{ color: '#e53935', fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1,
                            borderRadius: '14px',
                            bgcolor: RENK.zemin,
                            p: '6px 6px 6px 12px',
                            border: '1.5px solid transparent',
                            transition: 'all 0.2s',
                            '&:focus-within': {
                                bgcolor: '#fff',
                                border: `1.5px solid ${RENK.turuncu}`,
                                boxShadow: '0 0 0 3px rgba(255, 152, 0, 0.08)'
                            }
                        }}
                    >
                        <TextField
                            variant="standard"
                            placeholder="Mesajınızı yazın..."
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            InputProps={{ disableUnderline: true, sx: { fontSize: '0.9rem', lineHeight: 1.5 } }}
                        />
                        <IconButton
                            onClick={() => sendMessage()}
                            disabled={isTokenLoading || isStreaming || !input.trim()}
                            sx={{
                                width: 36,
                                height: 36,
                                p: 0,
                                ml: 1,
                                flexShrink: 0,
                                borderRadius: '10px',
                                color: '#fff',
                                bgcolor: input.trim() && !isStreaming ? RENK.turuncu : '#e0e0e0',
                                transition: 'background-color 0.2s',
                                '&:hover': { bgcolor: RENK.turuncuKoyu },
                                '&.Mui-disabled': { color: '#fff', bgcolor: '#e0e0e0' }
                            }}
                        >
                            {isTokenLoading || isStreaming
                                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                                : <SendIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                    </Box>
                </Box>
            </DialogActions>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}

export default TeknisyenGPTDialog;
